import React from "react";
import { Box, Paper, Typography, Chip, Button, IconButton, Link } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { GetClaimTransactionDocumentContent } from "../../Dashboard/DashboardAction";
import { RootState } from "../../../store/store";
import { getBufferData } from "../../../utils/Utility";
import { setClaimTransactionDocumentContent } from "../../Dashboard/DashboardSlice";

interface PolicyAction {
    id: string;
    label: string;
    params?: any;
    followup_message?: string;
}

interface TrackClaimCardProps {
    card: any;
    onAction?: (action: PolicyAction) => void;
}

const formatDisplayDate = (raw?: string) => {
    if (!raw) return undefined;
    // Accepts formats like: "2025-03-04 00:01:00 CDT" or "07/01/2024 05:36:15 CDT"
    const firstToken = raw.split(" ")[0];
    let d: Date | null = null;
    if (firstToken.includes("-")) {
        const [yyyy, mm, dd] = firstToken.split("-");
        if (yyyy && mm && dd) d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    } else if (firstToken.includes("/")) {
        const [mm, dd, yyyy] = firstToken.split("/");
        if (yyyy && mm && dd) d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    }
    if (!d || isNaN(d.getTime())) return undefined;
    const formatter = new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "2-digit",
        year: "numeric",
    });
    return formatter.format(d);
};

const TrackClaimCard: React.FC<TrackClaimCardProps> = ({ card, onAction }) => {    
    const dispatch = useAppDispatch();
    const { isSuccess, isError, calledReducerType } = useAppSelector((s: RootState) => s.common);
    const { claimTransactionDocumentContent } = useAppSelector((s: RootState) => s.dashboard);
    const [currentClickFile, setCurrentClickFile] = React.useState<{ name: string; addDate?: string; addTime?: string } | null>(null);

    const transactionsSection = (card?.sections || []).find(
        (s: any) => (s?.heading || "").toLowerCase().includes("transaction")
    );
    const documentsSection = (card?.sections || []).find(
        (s: any) => (s?.heading || "").toLowerCase().includes("document")
    );

const claimId =
  card.sections
    .find((section:any) => section.heading === "Claim Details")
    ?.items.find((item:any) => item.label === "Claim Id")?.value;
    
    const handleDownload = (doc: any) => {
        console.log("documentData", doc);
        
        // Use the same pattern as ClaimCard - always use Redux flow
        setCurrentClickFile({
            name: doc?.label || "document",
            addDate: typeof doc?.["Added On"] === "string" ? doc?.["Added On"] : undefined,
            addTime: (doc?.addTime as string) || undefined,
        });
        dispatch(setClaimTransactionDocumentContent(null));
        
        if (claimId) {
            dispatch(
                GetClaimTransactionDocumentContent({
                    claimId,
                    documentId: "Claims_Document",
                })
            );
        } else {
            alert("Claim ID not found");
        }
    };

    React.useEffect(() => {
        if (
            isSuccess &&
            calledReducerType === "dashboard/GetClaimTransactionDocumentContent" &&
            claimTransactionDocumentContent &&
            currentClickFile
        ) {
            console.log("documentData11", claimTransactionDocumentContent, currentClickFile);
            
            const bufferArray = getBufferData(claimTransactionDocumentContent);
            const url = URL.createObjectURL(
                new Blob([bufferArray], { type: "application/pdf" })
            );

            const downloadLink = document.createElement("a");
            downloadLink.download = `${currentClickFile?.name}-${currentClickFile?.addDate}-${currentClickFile?.addTime}.pdf`;
            downloadLink.href = url;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(url);
            setCurrentClickFile(null);
        }
    }, [
        calledReducerType,
        isSuccess,
        claimTransactionDocumentContent,
        currentClickFile,
    ]);

    React.useEffect(() => {
        if (
            isError &&
            calledReducerType === "dashboard/GetClaimTransactionDocumentContent"
        ) {
            alert(`Error in downloading document`);
        }
    }, [calledReducerType, isError]);

    const subtitle = card?.subtitle || "";
    const statusMatch = subtitle.match(/Status:\s*(\w+)/i);
    const status = statusMatch ? statusMatch[1].toLowerCase() : "";

    const statusMap: Record<
    string,
    { label: string; color: "default" | "warning" | "success" | "error" }
    > = {
    open: { label: "Open", color: "warning" },
    submitted: { label: "Submitted", color: "success" },
    closed: { label: "Closed", color: "error" },
    };

    const chipConfig = statusMap[status];

    if (!card) {
        return <></>;
    }

    return (
        <Paper
            elevation={0}
            sx={{
                width: { xs: "100%", md: "100%" },
                border: "1px solid #e5e7eb",
                borderRadius: { xs: 1, md: 2 },
                p: { xs: 1, md: 1.5 },
                bgcolor: "#ffffff",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 } }}>
                        {card?.title || "Track Claim"}
                    </Typography>
                </Box>
                {chipConfig && (
                        <Chip
                            label={chipConfig.label}
                            color={chipConfig.color}
                            size="small"
                            sx={{ fontWeight: 600, borderRadius: 9999 }}
                        />
                 )}
            </Box>

            {/* Transactions */}
            {transactionsSection && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 11, md: 12 }, color: "#374151", mb: 0.75 }}>
                        Transactions
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {(transactionsSection.items || []).map((item: any, idx: number) => {
                            const isLast = idx === (transactionsSection.items.length - 1);
                            const dateText = formatDisplayDate(item.label) || item.label;
                            const showContact = String(item.value).toLowerCase().includes("adjuster assigned");
                            return (
                                <Box key={`txn_${idx}`} sx={{ display: "grid", gridTemplateColumns: "84px 32px 1fr", alignItems: "start" }}>
                                    <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#374151" }}>{dateText}</Typography>
                                    <Box sx={{ position: "relative", height: "100%", display: "flex", justifyContent: "center", minHeight: 36 }}>
                                        <Box sx={{ width: 2, bgcolor: "#7d8186ff", position: "absolute", top: idx === 0 ? 12 : 0, bottom: isLast ? 12 : 0, left: "50%", transform: "translateX(-50%)" }} />
                                        <Box sx={{ width: 10, height: 10, bgcolor: "#7d8186ff", borderRadius: "50%", position: "relative" }} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: { xs: 11, md: 12 }, color: "#374151" }}>{String(item.value).replace(/\s*\(.*\)$/, "")}</Typography>
                                        {/* {showContact && (
                                            <Link sx={{ fontSize: { xs: 11, md: 12 }, color: "#374151", textDecoration: "underline" }}>
                                                Contact Person
                                            </Link>
                                        )} */}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* Documents */}
            {documentsSection && (
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 11, md: 12 }, color: "#374151", mb: 0.75 }}>
                        Documents
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {(documentsSection.items || []).map((doc: any, idx: number) => {
                            const addedOn = formatDisplayDate(doc["Added On"]);
                            const hasAction = true;
                            return (
                                <Paper
                                    key={`doc_${idx}`}
                                    elevation={0}
                                    sx={{
                                        border: "1px solid #e5e7eb",
                                        borderRadius: { xs: 1, md: 2 },
                                        p: { xs: 1, md: 1.25 },
                                        bgcolor: "#ffffff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                        <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: "#FDECEC", display: "flex", alignItems: "center", justifyContent: "center", color: "#991B1B", fontWeight: 700 }}>PDF</Box>
                                        <Box>
                                            <Typography sx={{ fontSize: { xs: 11, md: 12 }, color: "#111827", fontWeight: 600 }}>
                                                {doc.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>
                                                Date: {addedOn || "-"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        {/* <IconButton
                                            size="small"
                                            disabled={false}
                                            onClick={() => hasAction && handleDownload(doc)}
                                            aria-label="download"
                                        >
                                            <DownloadOutlinedIcon fontSize="small" />
                                        </IconButton> */}
                                        <Button
                                            size="small"
                                            variant="text"
                                            disabled={false}
                                            onClick={() => hasAction && handleDownload(doc)}
                                            sx={{ textTransform: "none", fontSize: { xs: 10, md: 12 } }}
                                        >
                                            Download
                                        </Button>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Paper>
    );
};

export default TrackClaimCard;


